import axios from "axios";
import { API_BASE_URL, API_BASE_URL1 } from "../Services/baseApiUrl";
import axiosInstance from "../../Hooks/axoisInstance";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token")?.trim();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const normalizeWardIdForPayload = (wardId) => {
  if (wardId === null || wardId === undefined || wardId === "") return "";

  const value = String(wardId).trim();
  if (/^\d+$/.test(value)) return value;

  if (/^ward-/i.test(value)) {
    const match = value.match(/(\d+)/);
    return match ? match[1] : "";
  }

  return value;
};

const parseResponseBody = async (response) => {
  const responseText = await response.text();
  if (!responseText) return "";

  try {
    return JSON.parse(responseText);
  } catch {
    return responseText;
  }
};

const submitFormData = async (url, formData, method = "POST") => {
  const headers = {
    Accept: "*/*",
    ...getAuthHeaders(),
  };

  const response = await fetch(url, {
    method,
    headers,
    body: formData,
  });

  const data = await parseResponseBody(response);

  if (!response.ok) {
    const error = new Error(
      typeof data === "string"
        ? data
        : data?.message || `Request failed with status ${response.status}`
    );
    error.response = { status: response.status, data };
    throw error;
  }

  return data;
};

const postFormData = async (url, formData) =>
  submitFormData(url, formData, "POST");

const putFormData = async (url, formData) =>
  submitFormData(url, formData, "PUT");

const buildIeIssueTypeFormData = (values, { isEdit = false } = {}) => {
  const formData = new FormData();
  formData.append("IssueType", values.issueType?.trim() ?? "");
  formData.append("KPITypeId", String(values.kpiTypeId ?? ""));
  formData.append("IsActive", String(Boolean(values.isActive)));

  if (isEdit) {
    const issueTypeId =
      values.ieIssueTypeId ??
      values.issueTypeId ??
      values.ieIssueId ??
      "";

    formData.append("IEIssueTypeId", String(issueTypeId));
  }

  return formData;
};

const buildSlaExceptionRuleFormData = (values, { isEdit = false } = {}) => {
  const formData = new FormData();
  formData.append("ZoneId", String(values.zoneId ?? ""));
  formData.append("WardId", String(values.wardId ?? values.circleId ?? ""));
  formData.append("ActionCategoryId", String(
    values.actionCategoryId ?? values.actionCategory ?? ""
  ));
  formData.append("Remarks", values.remarks?.trim() ?? "");
  formData.append("ValidFrom", values.validFrom ?? "");
  formData.append("ValidTo", values.validTo ?? values.validTill ?? "");
  formData.append("IsActive", String(Boolean(values.isActive)));

  if (isEdit) {
    const slaExceptionId =
      values.slaExceptionId ??
      values.slaExceptionRuleId ??
      values.id ??
      "";

    formData.append("SLAExceptionId", String(slaExceptionId));
  }

  return formData;
};

const buildSlaUserMatrixFormData = (values, { isEdit = false } = {}) => {
  const formData = new FormData();

  formData.append("UserId", String(values.userId ?? ""));
  formData.append("LevelId", String(values.levelId ?? values.level ?? ""));
  formData.append(
    "AlertBeforeEscalationInMin",
    String(
      values.alertBeforeEscalationInMin ??
        values.alertBeforeEscalationMin ??
        ""
    )
  );
  formData.append(
    "EscalationDurationInMin",
    String(
      values.escalationDurationInMin ?? values.escalationDurationMin ?? ""
    )
  );
  formData.append(
    "IsActive",
    String(Boolean(values.isActive)).toLowerCase()
  );

  if (isEdit) {
    formData.append(
      "TMatrixId",
      String(
        values.tMatrixId ??
          values.slaUserMatrixId ??
          values.slaUserMatrixMatrixId ??
          ""
      )
    );
  }

  return formData;
};

export const COMPLAINT_ENDPOINTS = {
  GET_SLA_CATEGORIES: `${API_BASE_URL}V1/Complaint/Master/GetSlacategory`,
  ADD_SLA_CATEGORY: `${API_BASE_URL}V1/Complaint/Master/AddSlacategory`,
  UPDATE_SLA_CATEGORY: `${API_BASE_URL}V1/Complaint/Master/UpdateSlacategory`,
  GET_GRIEVANCE_CATEGORIES: `${API_BASE_URL}V1/GMAM/Grievance/GetAllGrievanceCategory`,
  ADD_GRIEVANCE_CATEGORY: `${API_BASE_URL}V1/Complaint/Master/AddGrievancecategory`,
  UPDATE_GRIEVANCE_CATEGORY: `${API_BASE_URL}V1/Complaint/Master/UpdateGrievancecategory`,
  GET_IE_KPI_TYPE: `${API_BASE_URL}V1/Complaint/Master/GetIEKPITypeDetail`,
  ADD_IE_KPI_TYPE: `${API_BASE_URL}V1/Complaint/Master/AddIEKPITypeDetail`,
  UPDATE_IE_KPI_TYPE: `${API_BASE_URL}V1/Complaint/Master/UpdateIEKPITypeDetail`,
  GET_CITIZEN_COMPLAINT_OPERATION: `${API_BASE_URL}V1/Complaint/Operation/GetCitizenCompliantOperation`,
  GET_COMPLAINT_ASSIGNED_DETAILS_BY_ID: `${API_BASE_URL}V1/Complaint/Operation/GetCompliantAssignedDetailsById`,
  GET_GRIEVANCE_CATEGORIES_BY_SLA: `${API_BASE_URL}V1/Complaint/Operation/GetGrievanceCategoriesBySLA`,
  CITIZEN_COMPLIANT_REGISTER: `${API_BASE_URL}V1/Complaint/Operation/CitizenCompliantRegister`,
  UPDATE_CITIZEN_COMPLIANT_REGISTER: `${API_BASE_URL}V1/Complaint/Operation/UpdateCitizenCompliant`,
  GET_IE_ISSUE_TYPES: `${API_BASE_URL}V1/Complaint/Master/GetAllIEIssueTypeInformation`,
  ADD_IE_ISSUE_TYPE: `${API_BASE_URL}V1/Complaint/Master/AddIEIssueType`,
  UPDATE_IE_ISSUE_TYPE: `${API_BASE_URL}V1/Complaint/Master/UpdateIEIssueType`,
  GET_KPI_TYPE_DETAILS: `${API_BASE_URL}V1/Complaint/Master/GetKPITypeDetails`,
  GET_ALL_ZONES: `${API_BASE_URL}V1/Complaint/Master/GetAllZones`,
  GET_ALL_CIRCLES: `${API_BASE_URL}V1/Complaint/Master/GetAllCircles`,
  GET_ALL_ACTION_CATEGORIES: `${API_BASE_URL}V1/Complaint/Master/GetAllActionCategories`,
  GET_SLA_EXCEPTION_RULES: `${API_BASE_URL}V1/Complaint/Master/GetAllSLAExceptionRule`,
  ADD_SLA_EXCEPTION_RULE: `${API_BASE_URL}V1/Complaint/Master/AddSLAExceptionRule`,
  UPDATE_SLA_EXCEPTION_RULE: `${API_BASE_URL}V1/Complaint/Master/UpdateSLAExceptionRule`,
  GET_SLA_USER_MATRIX: `${API_BASE_URL}V1/Complaint/Master/GetAllSLAUserMatrix`,
  ADD_SLA_USER_MATRIX: `${API_BASE_URL}V1/Complaint/Master/AddSLAUserMatrix`,
  UPDATE_SLA_USER_MATRIX: `${API_BASE_URL}V1/Complaint/Master/UpdateSLAUserMatrix`,
  GET_ALL_LEVELS: `${API_BASE_URL}V1/Complaint/Master/GetAllLevels`,
  GET_SLA_USERS: `${API_BASE_URL}V1/Complaint/Master/GetSLAUsers`,
  GET_CIRCLE_BY_ZONE_ID: `${API_BASE_URL1}Attendance/GetCircleByZoneId`,
  GET_STATUS_DROPDOWNS: `${API_BASE_URL}IEComplaint/GetStatusDropdown`,
};

const toDataList = (response) => {
  const list =
    response?.data ??
    response?.result ??
    (Array.isArray(response) ? response : []);

  return Array.isArray(list) ? list : [];
};

export const getAllComplaintZones = async () => {
  const response = await axiosInstance.get(COMPLAINT_ENDPOINTS.GET_ALL_ZONES);
  return toDataList(response.data);
};

export const getAllComplaintCircles = async () => {
  const response = await axiosInstance.get(COMPLAINT_ENDPOINTS.GET_ALL_CIRCLES);
  return toDataList(response.data);
};

export const getAllActionCategories = async () => {
  const response = await axiosInstance.get(
    COMPLAINT_ENDPOINTS.GET_ALL_ACTION_CATEGORIES
  );
  return toDataList(response.data);
};

export const addIeIssueType = async (values) =>
  postFormData(
    COMPLAINT_ENDPOINTS.ADD_IE_ISSUE_TYPE,
    buildIeIssueTypeFormData(values)
  );

export const updateIeIssueType = async (values) =>
  putFormData(
    COMPLAINT_ENDPOINTS.UPDATE_IE_ISSUE_TYPE,
    buildIeIssueTypeFormData(values, { isEdit: true })
  );

export const getIeIssueTypeList = async ({
  pageIndex = 1,
  pageSize = 10,
  issueType = "",
} = {}) => {
  const formData = new FormData();
  formData.append("PageIndex", String(pageIndex));
  formData.append("PageSize", String(pageSize));

  if (issueType?.trim()) {
    formData.append("IssueType", issueType.trim());
  }

  return postFormData(COMPLAINT_ENDPOINTS.GET_IE_ISSUE_TYPES, formData);
};

export const getSlaExceptionRuleList = async ({
  pageIndex = 1,
  pageSize = 10,
  search = "",
} = {}) => {
  const formData = new FormData();
  formData.append("PageIndex", String(pageIndex));
  formData.append("PageSize", String(pageSize));

  if (search?.trim()) {
    formData.append("Search", search.trim());
  }

  return postFormData(COMPLAINT_ENDPOINTS.GET_SLA_EXCEPTION_RULES, formData);
};

export const getSlaUserMatrixList = async ({
  pageIndex = 1,
  pageSize = 10,
  search = "",
  zoneId = "",
  wardId = "",
} = {}) => {
  const formData = new FormData();
  formData.append("PageIndex", String(pageIndex));
  formData.append("PageSize", String(pageSize));

  if (search?.trim()) {
    formData.append("Search", search.trim());
  }

  if (zoneId) {
    formData.append("ZoneId", String(zoneId));
  }

  if (wardId) {
    formData.append("WardId", String(wardId));
  }

  return postFormData(COMPLAINT_ENDPOINTS.GET_SLA_USER_MATRIX, formData);
};

export const addSlaExceptionRule = async (values) =>
  postFormData(
    COMPLAINT_ENDPOINTS.ADD_SLA_EXCEPTION_RULE,
    buildSlaExceptionRuleFormData(values)
  );

export const updateSlaExceptionRule = async (values) =>
  postFormData(
    COMPLAINT_ENDPOINTS.UPDATE_SLA_EXCEPTION_RULE,
    buildSlaExceptionRuleFormData(values, { isEdit: true })
  );

export const buildCitizenComplaintRegisterFormData = (
  values = {},
  { isEdit = false } = {}
) => {
  const formData = new FormData();

  if (isEdit) {
    const complaintId =
      values.complaintId ??
      values.ComplaintId ??
      values.compliantId ??
      values.id ??
      "";

    formData.append("ComplaintId", String(complaintId));
    formData.append("CgNo", values.cgNo ?? "");
    formData.append(
      "WardId",
      normalizeWardIdForPayload(values.wardId ?? values.circleId ?? "")
    );
    formData.append(
      "RevisedWardNo",
      values.revisedWardNo ?? values.wardNo ?? ""
    );
    formData.append(
      "StreetLocation",
      (values.streetLocation ?? values.revisedStreetLocation ?? "").trim()
    );
    formData.append("Status", values.status ?? "");
    formData.append("ActionRemarks", values.actionRemarks?.trim() ?? "");
    formData.append("Address", values.address?.trim() ?? "");
    formData.append("Latitude", values.latitude?.toString() ?? "");
    formData.append("Longitude", values.longitude?.toString() ?? "");

    const imageFile = values.complaintImageFile;
    const isImageFile =
      imageFile instanceof File &&
      imageFile.size > 0 &&
      (["image/jpeg", "image/jpg", "image/png"].includes(imageFile.type) ||
        /\.(jpe?g|png)$/i.test(imageFile.name || ""));

    if (isImageFile) {
      formData.append("CloseImage", imageFile, imageFile.name);
    }

    return formData;
  }

  formData.append("ContactNo", values.contactNo ?? "");
  formData.append("CompliantName", values.complainantName?.trim() ?? "");
  formData.append("CompliantDetails", values.description?.trim() ?? "");
  formData.append("Latitude", values.latitude?.toString() ?? "");
  formData.append("Longitude", values.longitude?.toString() ?? "");
  formData.append(
    "CompliantTypeId",
    String(values.complaintTypeId ?? values.compliantTypeId ?? "")
  );
  formData.append("Address", values.address?.trim() ?? "");
  formData.append(
    "WardId",
    normalizeWardIdForPayload(values.wardId ?? values.circleId ?? "")
  );
  formData.append(
    "StreetLocation",
    (values.streetLocation ?? values.revisedStreetLocation ?? "").trim()
  );
  formData.append(
    "CompliantCategory",
    String(values.complaintCategoryId ?? values.complaintCategory ?? "")
  );

  const imageFile = values.complaintImageFile;
  const isImageFile =
    imageFile instanceof File &&
    imageFile.size > 0 &&
    (["image/jpeg", "image/jpg", "image/png"].includes(imageFile.type) ||
      /\.(jpe?g|png)$/i.test(imageFile.name || ""));

  if (isImageFile) {
    formData.append("Image", imageFile, imageFile.name);
  }

  return formData;
};

export const registerCitizenComplaint = async (values) =>
  postFormData(
    COMPLAINT_ENDPOINTS.CITIZEN_COMPLIANT_REGISTER,
    buildCitizenComplaintRegisterFormData(values)
  );

export const updateCitizenComplaint = async (values) =>
  putFormData(
    COMPLAINT_ENDPOINTS.UPDATE_CITIZEN_COMPLIANT_REGISTER,
    buildCitizenComplaintRegisterFormData(values, { isEdit: true })
  );

export const getComplaintCirclesByZoneId = async (zoneId) => {
  const response = await axiosInstance.get(
    `${COMPLAINT_ENDPOINTS.GET_CIRCLE_BY_ZONE_ID}/${zoneId}`
  );
  return Array.isArray(response.data) ? response.data : toDataList(response.data);
};

export const getAllSlaLevels = async () => {
  const response = await axiosInstance.get(COMPLAINT_ENDPOINTS.GET_ALL_LEVELS);
  return Array.isArray(response.data) ? response.data : toDataList(response.data);
};

export const getSlaUsers = async () => {
  const response = await axiosInstance.get(COMPLAINT_ENDPOINTS.GET_SLA_USERS);
  return Array.isArray(response.data) ? response.data : toDataList(response.data);
};

export const addSlaUserMatrix = async (values) =>
  postFormData(
    COMPLAINT_ENDPOINTS.ADD_SLA_USER_MATRIX,
    buildSlaUserMatrixFormData(values)
  );

export const updateSlaUserMatrix = async (values) =>
  postFormData(
    COMPLAINT_ENDPOINTS.UPDATE_SLA_USER_MATRIX,
    buildSlaUserMatrixFormData(values, { isEdit: true })
  );

export const getData = async (url, params = {}) => {
  const response = await axiosInstance.get(url, { params });
  return response.data;
};

export const postData = async (url, data = {}) => {
  const response = await axiosInstance.post(url, data);
  return response.data;
};

export const putData = async (url, data = {}) => {
  const response = await axiosInstance.put(url, data);
  return response.data;
};
